/** @jsx jsx */
import React, { type ReactNode, useCallback, useEffect, type MouseEvent } from 'react';
import { css, jsx } from '@compiled/react';
import Button from '@atlaskit/button';
import DropdownMenu, { DropdownItemGroup } from '@atlaskit/dropdown-menu';
import AddIcon from '@atlaskit/icon/glyph/add';
import AddonIcon from '@atlaskit/icon/glyph/addon';
import MoreIcon from '@atlaskit/icon/glyph/more';
import { Inline } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import AkToolTip from '@atlaskit/tooltip';
import { expVal } from '@atlassian/jira-feature-experiments';
import { useIntl } from '@atlassian/jira-intl';
import type { EcosystemContentPanel } from '@atlassian/jira-issue-view-common-types/src/ecosystem-types';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { messages } from './messages';

type Props = {
	actions: ReactNode[];
	apps: ReactNode;
	availableAddons?: EcosystemContentPanel[];
	setMenuOpen: (menuName: string, isOpen: boolean) => void;
	openMenu: string | null;
	quickActionListViewContainerRef?: { current: HTMLDivElement | null };
	canInstallAddons?: boolean;
};

export const QuickAddItemsCompact = (props: Props) => {
	const {
		actions,
		apps,
		availableAddons,
		setMenuOpen,
		openMenu,
		quickActionListViewContainerRef,
		canInstallAddons,
	} = props;
	const { formatMessage } = useIntl();
	const add = formatMessage(messages.add);
	const appsMessage = formatMessage(messages.apps);
	const availableApps = availableAddons && availableAddons.length >= 1;

	const plusIconOnlyQuickAddButton = expVal(
		'issue_view_quick_actions_revamp',
		'plusIconOnlyQuickAddButton',
		false,
	);

	const handleClick = useCallback(() => {
		setMenuOpen('addButton', false);
	}, [setMenuOpen]);

	useEffect(() => {
		const node = quickActionListViewContainerRef?.current;
		if (node) {
			node.addEventListener('click', handleClick);
		}

		return () => {
			if (node) {
				node.removeEventListener('click', handleClick);
			}
		};
	}, [quickActionListViewContainerRef, handleClick]);

	const { createAnalyticsEvent } = useAnalyticsEvents();

	const onClickWithAnalytics = useCallback(
		(originalOnClick?: (event: MouseEvent<HTMLElement>) => void, buttonState?: string) =>
			(event: MouseEvent<HTMLElement>) => {
				if (originalOnClick) {
					originalOnClick(event);
				}

				if (buttonState) {
					fireUIAnalytics(
						createAnalyticsEvent({}),
						'button clicked',
						`quickActionButtonRevamp${buttonState}`,
					);
				}
			},
		[createAnalyticsEvent],
	);
	const toggleMenuOpen: (menuName: string) => ({ isOpen }: { isOpen: boolean }) => void =
		useCallback(
			(menuName: string) =>
				({ isOpen }: { isOpen: boolean }) => {
					setMenuOpen(menuName, isOpen);
				},
			[setMenuOpen],
		);

	return (
		<Inline space="space.100">
			<DropdownMenu
				trigger={({ triggerRef, onClick, ...rest }) => (
					<AkToolTip content={formatMessage(messages.buttonAddContent)}>
						<Button
							{...rest}
							label={formatMessage(messages.buttonAddContent)}
							ref={triggerRef}
							onClick={onClickWithAnalytics(onClick, !plusIconOnlyQuickAddButton ? 'Add' : '+')}
							iconBefore={<AddIcon label={add} size="small" />}
							css={styles}
						>
							{!plusIconOnlyQuickAddButton && add}
						</Button>
					</AkToolTip>
				)}
				testId="issue-view-foundation.quick-add.quick-add-items-compact.add-button-dropdown"
				shouldRenderToParent
				onOpenChange={toggleMenuOpen('addButton')}
				isOpen={openMenu === 'addButton'}
			>
				<DropdownItemGroup>{actions}</DropdownItemGroup>
			</DropdownMenu>
			{(availableApps || canInstallAddons) && (
				<DropdownMenu
					trigger={({ triggerRef, onClick, ...rest }) => (
						<AkToolTip
							content={
								availableApps
									? formatMessage(messages.buttonAddApps)
									: formatMessage(messages.buttonAddAppsNoApps)
							}
						>
							<Button
								{...rest}
								label={
									availableApps
										? formatMessage(messages.buttonAddApps)
										: formatMessage(messages.buttonAddAppsNoApps)
								}
								ref={triggerRef}
								onClick={onClickWithAnalytics(onClick, availableApps ? 'Apps' : '...')}
								iconBefore={
									availableApps ? (
										<AddonIcon label={appsMessage} size="small" />
									) : (
										<MoreIcon label={appsMessage} size="small" />
									)
								}
								css={styles}
							>
								{availableApps && appsMessage}
							</Button>
						</AkToolTip>
					)}
					testId="issue-view-foundation.quick-add.quick-add-items-compact.apps-button-dropdown"
					onOpenChange={toggleMenuOpen('appsButton')}
					isOpen={openMenu === 'appsButton'}
				>
					{apps}
				</DropdownMenu>
			)}
		</Inline>
	);
};

const styles = css({
	paddingLeft: token('space.075', '6px'),
	paddingRight: token('space.075', '6px'),
});
export default QuickAddItemsCompact;
